import type { APIErrorCode, ResourceMapNode, ResourceMapResponse } from "@k8s-dashboard/contracts";
import { HttpError } from "@/api/client";

/**
 * Resource Map 고정 문구·상태 블록 (ADR 0020, #8)
 * --------------------------------------------------------------------------
 * 서버 오류 코드·관계 상태·잘림 사유를 **고정 한국어 문구**로 옮깁니다.
 * 임의 서버 문자열은 화면에 싣지 않습니다.
 */

export const RELATION_STATE_LABEL: Record<string, string> = {
  supported: "지원",
  partial: "부분 지원",
  unsupported: "미지원",
};

export const TRUNCATION_REASON_LABEL: Record<string, string> = {
  depth: "깊이 상한",
  nodes: "노드 상한",
  edges: "간선 상한",
  bytes: "응답 크기 상한",
  work: "작업량 상한",
  deadline: "시간 상한",
  fanout: "확장 상한",
};

export type Notice = { title: string; detail: string; tone: "neutral" | "degraded" | "forbidden" | "error" };

/**
 * 서버 오류 코드를 고정 문구로 옮깁니다. 임의 서버 문자열은 화면에 싣지 않습니다.
 * 코드 어휘의 정본은 계약의 APIErrorCode 하나이고, HttpError.body가 그 타입이므로
 * 여기서는 넓힘·캐스트 없이 그대로 분기합니다. 모르는 코드는 고정 fallback입니다.
 */
export function errorNotice(error: unknown): Notice {
  const code: APIErrorCode | "" = error instanceof HttpError ? error.body.code : "";
  switch (code) {
    case "resource_syncing":
      return {
        title: "관계 그래프를 동기화하는 중입니다",
        detail: "0건이 아니라 준비 중입니다 — 잠시 후 다시 조회하세요.",
        tone: "degraded",
      };
    case "resources_unavailable":
      return {
        title: "이 배포에서는 리소스 탐색을 사용할 수 없습니다",
        detail: "중앙(central) 모드이거나 기능이 꺼져 있습니다.",
        tone: "degraded",
      };
    case "forbidden":
    case "cluster_access_denied":
      return {
        title: "관계 그래프 조회 권한이 없습니다",
        detail: "데이터가 없는 것이 아니라 조회가 거절되었습니다.",
        tone: "forbidden",
      };
    case "namespace_access_denied":
      return {
        title: "focus namespace에 대한 권한이 없습니다",
        detail: "데이터가 없는 것이 아니라 권한 Scope 밖입니다.",
        tone: "forbidden",
      };
    case "cluster_scope_required":
      return {
        title: "클러스터 범위 focus는 클러스터 전체 권한이 필요합니다",
        detail: "namespace 권한만으로는 cluster 범위 그래프를 열 수 없습니다.",
        tone: "forbidden",
      };
    case "resource_forbidden":
      return {
        title: "서버에 이 리소스의 조회 권한이 없습니다",
        detail: "대시보드 ServiceAccount의 RBAC에 이 리소스가 없습니다 — 사용자 권한 문제가 아닙니다.",
        tone: "forbidden",
      };
    case "resource_unsupported":
      return {
        title: "이 API는 metadata 전용 조회를 지원하지 않습니다",
        detail: "서버가 전체 객체 watch로 물러나지 않고 미지원으로 알립니다.",
        tone: "degraded",
      };
    case "resource_not_served":
    case "resource_not_allowlisted":
      return {
        title: "이 리소스는 관계 그래프 대상이 아닙니다",
        detail: "탐색 대상으로 등록되지 않았거나 클러스터가 이 API를 제공하지 않습니다.",
        tone: "neutral",
      };
    case "object_too_large":
      return {
        title: "그래프 응답이 크기 한도를 넘었습니다",
        detail: "depth를 줄이거나 관계 필터를 좁혀 다시 조회하세요.",
        tone: "degraded",
      };
    case "not_found":
      return {
        title: "목록에 없는 focus입니다",
        detail: "삭제되었거나 캐시에서 빠졌습니다. Explorer에서 항목을 다시 선택하세요.",
        tone: "neutral",
      };
    case "uid_mismatch":
      return {
        title: "같은 이름의 다른 객체로 교체되었습니다",
        detail: "Explorer 목록을 새로고침한 뒤 항목을 다시 선택하세요.",
        tone: "error",
      };
    case "scope_too_broad":
      return {
        title: "권한 Scope가 그래프 조회 상한을 넘습니다",
        detail: "이 계정의 namespace 수가 서버 상한을 넘어 관계 그래프를 계산할 수 없습니다.",
        tone: "degraded",
      };
    case "query_rejected":
      return {
        title: "요청 보호 한도를 초과했습니다",
        detail: "잠시 후 다시 조회하세요.",
        tone: "degraded",
      };
    case "query_timeout":
      return {
        title: "관계 그래프 조회 시간 한도를 초과했습니다",
        detail: "서버의 로컬 조회 상한입니다. 잠시 후 다시 조회하세요.",
        tone: "degraded",
      };
    case "invalid_filter":
      return {
        title: "요청 값이 서버 상한이나 형식을 벗어났습니다",
        detail: "focus 식별자와 depth를 확인하세요.",
        tone: "error",
      };
    default:
      return {
        title: "관계 그래프를 불러오지 못했습니다",
        detail: "일시적인 오류일 수 있습니다. 다시 조회하세요.",
        tone: "error",
      };
  }
}

export function StateBlock({ notice, role }: { notice: Notice; role?: "status" | "alert" }) {
  const cls = notice.tone === "neutral" ? "state" : `state state--${notice.tone}`;
  return (
    <div className={cls} role={role ?? (notice.tone === "forbidden" || notice.tone === "error" ? "alert" : "status")}>
      <span className="state__glyph" aria-hidden="true">
        {notice.tone === "neutral" ? "✓" : notice.tone === "forbidden" ? "!" : "▲"}
      </span>
      <span className="state__title">{notice.title}</span>
      <span className="state__detail">{notice.detail}</span>
    </div>
  );
}

export function nodeLabel(node: ResourceMapNode): string {
  const where = node.namespace ? `${node.namespace}/` : "";
  const resolved = node.resolved ? "" : " (미해석)";
  return `${node.kind} ${where}${node.name}${resolved}`;
}

export function scopeText(scope: ResourceMapResponse["appliedScope"]): string {
  const where = scope.namespaces === "all" ? "클러스터 전체" : scope.namespaces.join(", ");
  return `${scope.clusterId} · ${where}`;
}
